import { canv } from "@/canv/global";
import { generateUID } from "@/canv/utils";
import type { elementBase } from "@/elementBase/elementBase";
import type { cardWidget } from "@/elements/card/card";
import type { imageWidget } from "@/elements/image";
import type { shapeWidget } from "@/elements/shape";
import type { stickerWidget } from "@/elements/sticker";
import type { textWidget } from "@/elements/text/text";
import type { NewVoteState } from "./types";

export const getDefaultNewVoteState = (): NewVoteState => ({
    votesCount: 1,
    timeCount: 1,
    oneVotePerObject: false,
    widgetsList: [
        { name: "Стикеры", value: "sticker", selectedCount: 0, candidateCount: 0 },
        { name: "Фигуры", value: "shape", selectedCount: 0, candidateCount: 0 },
        {
            name: "Изображения",
            value: "image",
            selectedCount: 0,
            candidateCount: 0,
        },
        { name: "Текст", value: "text", selectedCount: 0, candidateCount: 0 },
        { name: "Карточки", value: "card", selectedCount: 0, candidateCount: 0 },
    ],
    text: "Голосование 1",
});

export const createVoteBuildEvent = (state: NewVoteState) => {
    return [
        {
            type: "build",
            action: "create",
            entities: [createEntityForVoteBuildEvent(state)],
        },
    ];
};

const createEntityForVoteBuildEvent = (state: NewVoteState) => {
    return {
        entityType: "vote",
        UID: generateUID(),
        text: `Голосование ${canv.canvasController.pastVotes.length + 1}`,
        start: new Date(),
        duration: state.timeCount * 60,
        votePerAccount: state.votesCount,
        oneVotePerItem: state.oneVotePerObject,
        isResolved: false,
        creatorToken: canv.getInitDataProp("userToken"),
        creatorName: canv.getInitDataProp("userName"),
        children: createEntityChildForVoteBuildEvent(),
    };
};

const createEntityChildForVoteBuildEvent = () => {
    if (canv.appInterface.voteWindow.screen === null) {
        throw new Error("Ошибка: не найдена область голосования");
    }

    const { candidates, selectedGroupUIDs } = canv.appInterface.voteWindow.screen;
    const result = {};

    for (const groupUID of selectedGroupUIDs) {
        const group = candidates.get(groupUID);

        // выбранная группа есть в кандидатах
        if (group) {
            const widget = group.onlyChild();

            if (!widget) {
                throw new Error("Ошибка: группа не itemWrapper");
            }

            result[widget.UID] = {
                linkToOriginalUID: widget.UID,
                widgetData: getWidgetData(widget),
                votes: [],
            };
        }
    }

    return result;
};

const getWidgetData = <T extends elementBase>(widget: T) => {
    if (widget.constructorName === "shape") {
        const currentWidget = widget as unknown as shapeWidget;

        return {
            UID: currentWidget.UID,
            entityType: "shape",
            shapeType: currentWidget.mode,
            color: currentWidget.color,
            borderColor: currentWidget.borderColor,
            text: (currentWidget.textPart as unknown as textWidget)?.getText(),
            x: currentWidget.startX,
            y: currentWidget.startY,
        };
    }

    if (widget.constructorName === "image") {
        const currentWidget = widget as unknown as imageWidget;

        return {
            UID: currentWidget.UID,
            entityType: "media",
            imageUrl: currentWidget.src,
            text: "",
            width: currentWidget.width,
            height: currentWidget.height,
        };
    }

    if (widget.constructorName === "text") {
        const currentWidget = widget as unknown as textWidget;

        return {
            UID: currentWidget.UID,
            entityType: "text",
            text: currentWidget.getText(),
            font: currentWidget.fontStyle,
            fontSize: currentWidget.fontSize,
        };
    }

    if (widget.constructorName === "sticker") {
        const currentWidget = widget as unknown as stickerWidget;

        return {
            UID: currentWidget.UID,
            entityType: "sticker",
            color: currentWidget.color,
            text: (currentWidget.textPart as unknown as textWidget)?.getText(),
            x: currentWidget.startX,
            y: currentWidget.startY,
        };
    }

    if (widget.constructorName === "card") {
        const currentWidget = widget as unknown as cardWidget;

        return {
            UID: currentWidget.UID,
            entityType: "card",
            text: (currentWidget.textTitle as unknown as textWidget)?.getText(),
            progress: currentWidget.progress,
            x: currentWidget.startX,
            y: currentWidget.startY,
        };
    }
};
